import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../lib/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [impersonatedTenant, setImpersonatedTenant] = useState(null);

  useEffect(() => {
    // Check for saved user
    const savedUser = localStorage.getItem('user');
    const token = localStorage.getItem('token');
    if (savedUser && token) {
      setUser(JSON.parse(savedUser));
    }
    
    // Check for impersonated tenant
    const savedImpersonation = localStorage.getItem('impersonatedTenant');
    if (savedImpersonation) {
      setImpersonatedTenant(JSON.parse(savedImpersonation));
    }
    
    setLoading(false);
  }, []);

  const login = async (companyId, email, password) => {
    const response = await authAPI.login({ companyId, email, password });
    const { accessToken, user: userData } = response.data;
    localStorage.setItem('token', accessToken);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('impersonatedTenant');
    setUser(null);
    setImpersonatedTenant(null);
  };

  const clearImpersonation = () => {
    localStorage.removeItem('impersonatedTenant');
    setImpersonatedTenant(null);
  };

  const value = {
    user,
    loading,
    login,
    logout,
    isAuthenticated: !!user,
    impersonatedTenant,
    setImpersonatedTenant: (tenant) => {
      if (tenant) {
        localStorage.setItem('impersonatedTenant', JSON.stringify(tenant));
      }
      setImpersonatedTenant(tenant);
    },
    clearImpersonation,
    // Helper to check if user is superadmin
    isSuperAdmin: user?.role === 'superadmin',
    // Helper to check if user is admin
    isAdmin: ['admin', 'company_admin', 'superadmin'].includes(user?.role),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};